import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getAuth, signInWithCustomToken, multiFactor } from 'firebase/auth';

import { selectFirebaseUser, authorize, getDbInfo, logout, resetStatus, reset, selectSignupStatus } from '_redux/slices/main-screen/user';
import AuthRouter from './auth-router';
import AppRouter from './app-router';
import OnboardingRouter from './onboarding-router';
import RemoteCheckinPage from '_scenes/remotecheckin';

import 'firebase/firestore';
import { app } from '_utils/firebase-app';

import { CustomSpinner } from '_atoms';

import { SESSION_MAX_DURATION_MS } from '_utils/constants';
import { closePortalSession, getRemainingSessionMs } from '_services/session-service';

//assistance-bookings reducers
import { reset as HistoryReset } from '_redux/slices/assistance-bookings/bookings-history';
import { reset as BookingFormReset } from '_redux/slices/assistance-bookings/booking-form';
import { reset as RequestTableReset } from '_redux/slices/assistance-bookings/booking-requests-table';
import { reset as AdditionalPassengerReset } from '_redux/slices/assistance-bookings/additional-passenger';
//bookings reducers
import { reset as TableReset } from '_redux/slices/prm-bookings/bookings-table';
import { reset as BookingsReset } from '_redux/slices/prm-bookings/bookings';
import { reset as CalendarReset } from '_redux/slices/prm-bookings/calendar';
import { reset as ArrivalsReset } from '_redux/slices/prm-bookings/arrivals';
import { reset as DeparturesReset } from '_redux/slices/prm-bookings/departures';
import { reset as BookingForecastReset } from '_redux/slices/prm-bookings/booking-forecast';
import { reset as BookingCountReset } from '_redux/slices/prm-bookings/bookings-count';
import { reset as FilterReset } from '_redux/slices/prm-bookings/filter';
import { reset as FlightBookingsReset } from '_redux/slices/prm-bookings/flights';
import { reset as SeasonalVarianceReset } from '_redux/slices/prm-bookings/seasonal-variance';
//main-screens reducers
import { reset as ScreenReset } from '_redux/slices/main-screen/render-screen';
import { reset as ModalReset } from '_redux/slices/main-screen/modal';
import { reset as ScreenLookReset } from '_redux/slices/main-screen/screen-lock';
import { reset as UserReset } from '_redux/slices/main-screen/user';
import { reset as WarningModalReset } from '_redux/slices/main-screen/warning-modal';
//analytics reducers
import { reset as ReviewsReset } from '_redux/slices/analytics/reviews';
import { reset as NewUserReset } from '_redux/slices/analytics/new-users';
import { reset as ReviewNameReset } from '_redux/slices/analytics/review-name';
import { reset as WeatherReset } from '_redux/slices/analytics/weather';
//beacons reducers
import { reset as BeaconsReset } from '_redux/slices/beacons/beacons';
//flights reducers
import { reset as FlightReset } from '_redux/slices/flights/flight-information';
//notifications reducers
import { reset as NotificationsReset } from '_redux/slices/notifications/notifications';
//prm-clubs reducers
import { reset as RewardsReset } from '_redux/slices/prm-club/rewards';
import { reset as RewardsAnalyticsReset } from '_redux/slices/prm-club/analytics';
//profile reducers
import { reset as AdminTableReset } from '_redux/slices/profile/admin-table';
import { reset as AirportInfoReset } from '_redux/slices/profile/airport-information';
import { reset as FeedbackReset } from '_redux/slices/profile/feedback';
import { reset as UserProfileReset } from '_redux/slices/profile/user-profile';
//QA reducers
import { reset as QaReset } from '_redux/slices/qa/qa';
//timeline reducers
import { reset as EmployeeReset } from '_redux/slices/timeline/employees';
import { reset as FlightScannerReset } from '_redux/slices/timeline/flight-scanner';
import { reset as SelectedTaskReset } from '_redux/slices/timeline/selected-task';
import { reset as TimelineAreaReset } from '_redux/slices/timeline/timeline-areas';
import { reset as TimelineDataReset } from '_redux/slices/timeline/timeline-data';
import { reset as resetTimeLineControl } from "_redux/slices/timeline/timeline-control";
import { reset as resetTimeLineDashboard } from "_redux/slices/timeline/timeline-dashboard";
//websites reducers
import { reset as UserBookingsReset } from '_redux/slices/website/userbookings';
import { reset as ContactReset } from '_redux/slices/website/contact';
import { reset as TransactionsReset } from '_redux/slices/website/transactions';
import { reset as QrHandlerReset } from '_redux/slices/website/qr-handler';
//messages reducers
import { selectCustomAuthToken, getCustomAuthToken, reset as MessagingReset } from '_redux/slices/messaging/messaging';
import { reset as resetTimelineReports } from '_redux/slices/timeline-reports/timeline-reports';
import { reset as resetStaffEquipment } from "_redux/slices/timeline/staff-equipments"
import { reset as resetRejectReasons } from "_redux/slices/timeline/reject-reasons"
import { reset as resetSecurity } from "_redux/slices/security/security"

const auth = getAuth();

/**
 * Determine which onboarding step (if any) a user must complete.
 * Invited accounts and accounts flagged via Staff Management (Require MFA &
 * Password Reset) carry tempPassword / mfaRequired custom claims.
 */
const getOnboardingStep = async (firebaseUser) => {
  if (!firebaseUser) return null;

  try {
    const tokenResult = await firebaseUser.getIdTokenResult();
    const claims = tokenResult?.claims || {};

    const isInvitedAccount = claims.tempPassword === true || claims.mfaRequired === true;
    if (!isInvitedAccount) return null;

    // 1. email verification
    if (!firebaseUser.emailVerified) return 'email-verification';

    // 2. temporary password change
    if (claims.tempPassword === true) return 'password-reset';

    // 3. MFA enrollment (checked against actual enrolled factors, not just claims)
    if (claims.mfaRequired === true) {
      const enrolledFactors = multiFactor(firebaseUser).enrolledFactors || [];
      if (enrolledFactors.length === 0) return 'mfa-setup';
    }

    return null;
  } catch (e) {
    console.log('Error determining onboarding step', e);
    return null;
  }
};

const RootRouter = () => {

  const [initializing, setInitializing] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(null);
  const user = useSelector(selectFirebaseUser);
  const registerStatus = useSelector(selectSignupStatus)
  const dispatch = useDispatch();
  const customAuthToken = useSelector(selectCustomAuthToken)

  // monitor for log in
  useEffect(async () => {

    // called when auth state changed
    const subscriber = auth.onAuthStateChanged(async (result) => {

      //console.log('Auth state changed...', result)

      // inside of result the user object
      dispatch(authorize(result));

      if (result != null) {

        //console.log("User logged in with uid: ", result.uid, '... get db info');

        // always mint fresh token to avoid stale token with no custom claims
        const tokenResult = await result.getIdTokenResult(true);
        console.log('Portal user claims:', tokenResult.claims);

        // using firebase info to get user db info
        dispatch(getDbInfo(result.uid))

        // check if the account still needs onboarding (email / password / MFA)
        const step = await getOnboardingStep(result);
        setOnboardingStep(step);
      }
      else {
        setOnboardingStep(null);
      }

      setTimeout(() => {
        if (initializing) setInitializing(false);
      });
    });
    return subscriber;
  }, []);

  const handleLogout = async () => {
    // Close the portal session doc before wiping storage, otherwise this
    // timeout logout leaves the session open (logoutTime/closedBy null) while
    // localStorage.clear() removes the anchor keys — the "auto logout, doc
    // never closed" bug. closePortalSession reads prm_session_id from storage,
    // so it must run BEFORE the clear.
    await closePortalSession('timeout');
    localStorage.clear();
    // logout and change page
    dispatch(logout());

    // reset all slices
    dispatch(reset());
    dispatch(resetStatus());
    dispatch(BookingFormReset());
    dispatch(RequestTableReset());
    dispatch(AdditionalPassengerReset());
    dispatch(DeparturesReset());
    dispatch(ArrivalsReset());
    dispatch(BookingForecastReset());
    dispatch(BookingCountReset());
    dispatch(FlightBookingsReset());
    dispatch(FilterReset());
    dispatch(SeasonalVarianceReset());
    dispatch(ModalReset());
    dispatch(ScreenLookReset());
    dispatch(WarningModalReset());
    dispatch(NewUserReset());
    dispatch(ReviewNameReset());
    dispatch(WeatherReset());
    dispatch(BeaconsReset());
    dispatch(FlightReset());
    dispatch(NotificationsReset());
    dispatch(RewardsAnalyticsReset());
    dispatch(AdminTableReset());
    dispatch(AirportInfoReset());
    dispatch(FeedbackReset());
    dispatch(UserProfileReset());
    dispatch(QaReset());
    dispatch(EmployeeReset());
    dispatch(FlightScannerReset());
    dispatch(SelectedTaskReset());
    dispatch(TimelineAreaReset());
    dispatch(TimelineDataReset());
    dispatch(resetTimeLineControl());
    dispatch(resetTimeLineDashboard());
    dispatch(ContactReset());
    dispatch(TransactionsReset());
    dispatch(QrHandlerReset());
    dispatch(UserReset())
    dispatch(HistoryReset())
    dispatch(TableReset())
    dispatch(BookingsReset())
    dispatch(CalendarReset())
    dispatch(ScreenReset())
    dispatch(ReviewsReset())
    dispatch(RewardsReset())
    dispatch(UserBookingsReset())
    dispatch(MessagingReset())
    dispatch(resetTimelineReports())
    dispatch(resetStaffEquipment())
    dispatch(resetRejectReasons())
    dispatch(resetSecurity())
  }

  /**
   * Client-side session expiry enforcement + Signing into the secondary Firebase app
   */
  useEffect(() => {
    let userSessionTimeout = null;

    const run = async () => {
      try {
        if (!user) {
          setInitializing(false);
          return;
        }

        // Wait for live Firebase Auth — Redux user can be rehydrated from persist
        // before auth.currentUser is restored, which would make getToken() throw.
        const currentUser = auth.currentUser;
        if (!currentUser) {
          return;
        }

        // enforces session timeout
        //
        // Anchored to the portal session's loginTime, the same anchor sidebar.js
        // uses, so the two enforcers can never disagree about when the 12h30m wall
        // falls. This previously read the Firebase auth_time claim, which records
        // the last real *authentication* rather than the last sign-in to the
        // portal. On a shared kiosk tablet (dublinairport@prmassist.com) that had
        // been signed into Firebase since the morning, auth_time did not reset when
        // the night allocator signed in, so the cap expired mid-shift on someone
        // who had just sat down — and because getIdTokenResult() serves a CACHED
        // token, every attempt to sign back in read the same stale auth_time and
        // was kicked again within seconds. Reported 7-8 Sep 2026.
        //
        // Only schedules; it never logs out on the spot. sidebar.js owns the
        // already-expired case (it shows the timeout modal), and the server-side
        // closeExpiredSessionsCron is the backstop. Two components racing to call
        // handleLogout is what made this hard to read in the first place.
        try {
          const remaining = getRemainingSessionMs(SESSION_MAX_DURATION_MS, currentUser.uid);
          if (remaining !== null && remaining > 0) {
            userSessionTimeout = setTimeout(() => handleLogout(), remaining);
          }
        } catch (e) {
          console.log('Error scheduling session timeout', e);
        }

        // if custom token exists > signin into secondary app else getcustom token
        if (customAuthToken) {
          const appAuth = getAuth(app);
          try {
            await signInWithCustomToken(appAuth, customAuthToken);
          } catch (error) {
            // secondary messaging auth — ignore failures here
          }
        } else {
          dispatch(getCustomAuthToken());
        }
      } catch (e) {
        console.log(e);
      }
    };

    run();

    return () => {
      if (userSessionTimeout) clearTimeout(userSessionTimeout);
    };
  }, [user, customAuthToken]);

  useEffect(() => {
    if (registerStatus == 'succeded' && user != null) {
      dispatch(getDbInfo(user.uid))
    }
  }, [registerStatus]);

  if (initializing) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center', // horizontally center
          width: '100%',
          height: '100vh', // Ensure this takes up the entire screen height
          position: 'absolute', // Ensure it's taking up the full viewport
          top: 0,
          left: 0,
        }}>
        <CustomSpinner rowCenter={true} />
      </div>
    );
  }

  if (window.location.pathname.startsWith('/remotecheckin')) {
    return <RemoteCheckinPage />;
  }

  // invited accounts must complete onboarding before reaching the portal
  if (user && onboardingStep) {
    return <OnboardingRouter step={onboardingStep} />;
  }

  return (user) ? <AppRouter /> : <AuthRouter />

};

export default RootRouter;
